import * as React from "react";
// Usiamo gli hook di React per gestire lo stato
import { useState, useEffect } from "react";

import config from "./config"; 
import { getProfileFromToken } from "./config"; 
import { useNavigate } from "react-router-dom";
import "../index.css"; 
import MHeader from "./header";
import Mfooter from "./footer";

// Mock di localStorage per ambienti dove non è disponibile (come il server-side)
// In un'app reale, questo non è necessario se 'localStorage' è sempre disponibile
const safeLocalStorage = (typeof window !== 'undefined' && window.localStorage)
  ? window.localStorage
  : {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {}
    };
// --- Fine Mock ---

/**
 * Componente riutilizzabile per mostrare e gestire dati da una tabella SQL.
 * @param {object} props
 * @param {string} props.tableName - Il nome della tabella da caricare (es. "luoghi", "bimobj").
 */

function DataTable({ tableName }) {
  const [data, setData] = React.useState([]);
  const [headers, setHeaders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Funzione per caricare i dati
  const fetchData = React.useCallback(async () => {

    
    
    setLoading(true);
    setError(null);
    const token = safeLocalStorage.getItem("token");

    try {
      // L'endpoint è il nome della tabella (es. /api/luoghi)
      const response = await fetch(`${config.apiUrl}/${tableName}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Errore di rete: ${response.statusText}`);
      }

      const result = await response.json();

      if (Array.isArray(result) && result.length > 0) {
        // Estrai dinamicamente gli header (colonne) dal primo record
        setHeaders(Object.keys(result[0]));
        setData(result);
      } else {
        setData([]);
        setHeaders([]);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Errore sconosciuto");
      }
      console.error(`Errore nel fetch per ${tableName}:`, err);
    } finally {
      setLoading(false);
    }
  }, [tableName]); // Dipendenza: ricarica se 'tableName' cambia

  // Carica i dati al primo render e quando 'tableName' cambia
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Funzione per gestire la cancellazione
  const handleDelete = async (idToDelete) => {
    // 1. Chiedi conferma
    if (!window.confirm(`Sei sicuro di voler eliminare il record ${idToDelete} dalla tabella ${tableName}?`)) {
      return;
    }

    const token = safeLocalStorage.getItem("token");

    try {
      // 2. Chiama l'API di cancellazione
      // Ho presunto un endpoint '/delete' che accetta JSON
      const response = await fetch(`${config.apiUrl}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          tableName: tableName,
          id: idToDelete
        })
      });

      if (!response.ok) {
        throw new Error('Errore durante la cancellazione.');
      }

      // 3. Rimuovi l'elemento dallo stato locale (per aggiornare l'UI)
      // Trova l'identificatore univoco (id, id_luogo, idbimobj, etc.)
      const idKey ="label";
      
      if (!idKey) {
        console.error("Impossibile trovare una chiave ID per l'eliminazione, ricarico i dati...");
        fetchData(); // Fallback: ricarica tutto
        return;
      }
      
      setData(prevData => prevData.filter(row => row[idKey] !== idToDelete));
      alert('Record eliminato con successo.');
      fetchData();

    } catch (err) {
      if (err instanceof Error) {
         console.error('Errore API cancellazione:', err);
         alert(`Errore: ${err.message}`);
      } else {
         console.error('Errore API cancellazione:', err);
         alert(`Errore sconosciuto durante la cancellazione.`);
      }
    }
  };
  
  // Trova la chiave ID primaria per passarla al bottone
  // Diamo priorità a id_NOME, poi id, poi NOMEid
  const primaryIdKey = React.useMemo(() => {
     if (!headers.length) return null;
     const foundKey = "value";
     return foundKey || headers[0]; // Ritorna la prima colonna se non trova un ID
  }, [headers]);


  // --- Rendering del componente ---

  if (loading) {
    return <div>Caricamento dati per "{tableName}"...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Errore: {error}</div>;
  }

  if (data.length === 0) {
    return <div>Nessun record trovato per "{tableName}".</div>;
  }

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
        
      <h3>Tabella: {tableName}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc' }}>
        <thead>
          <tr style={{ backgroundColor: '#f4f4f4' }}>
            {headers.map(header => (
              <th key={header} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                {header}
              </th>
            ))}
            <th style={{ border: '1px solid #ddd', padding: '8px' }}>Azione</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {headers.map(header => (
                <td key={`${rowIndex}-${header}`} style={{ border: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {/* Limita la lunghezza del testo per leggibilità */}
                  {String(row[header]).length > 50 ? String(row[header]).substring(0, 50) + '...' : row[header]}
                </td>
              ))}
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                {primaryIdKey && (
                  <button 
                    onClick={() => handleDelete(row[primaryIdKey])}
                    style={{ color: 'red', cursor: 'pointer', background: 'none', border: 'none', padding: 0, textDecoration: 'underline' }}
                  >
                    Elimina
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Componente App di Esempio per mostrare l'uso
export default function App() {
  const [currentTable, setCurrentTable] = React.useState('luoghi');

  const navigate = useNavigate();
    React.useEffect(() => {
    let profile = getProfileFromToken();
    if (profile !== "Admin") {
      navigate("/");
    }
  }, [navigate]);
  return (
    <div className="App"> 
        <h2  className="App-header">{config.appName}</h2>
        <MHeader/>
      <h1>Gestione Tabelle</h1>
      <p>Seleziona una tabella da visualizzare:</p>
      
      <select 
        value={currentTable} 
        onChange={(e) => setCurrentTable(e.target.value)}
        style={{ padding: '8px', marginBottom: '20px', fontSize: '1rem' }}
      >
        <option value="luogo">Luoghi</option>
        <option value="layer">Layer</option>
        <option value="bimobj">Oggetti</option>
        <option value="biminfo">Caratteristiche(info) oggetti </option>
        <option value="infoall">Allegati ad Info</option>
        <option value="infoval">Misurazioni di Carattteristiche</option>
      </select>

      {/* Il componente DataTable si aggiornerà quando 'currentTable' cambia */}
      <DataTable tableName={currentTable} />
      <Mfooter />
    </div>
  );
}

